import React from 'react'
import { LandingHeader } from '@/modules/landing/components/HeroSection6'
import LandingFooter from '@/modules/landing/components/LandingFooter'
import { Button, Card, CardContent, CardHeader } from '@/shared/ui'
import { Wallet, TrendingUp, ShieldCheck, BarChart3, Users, PieChart } from 'lucide-react'
import AffiliateApplicationModal from '../components/AffiliateApplicationModal'
import SimpleFeedbackModal from '../components/SimpleFeedbackModal'
import {
  affiliateLandingStats,
  affiliateLandingBenefits,
  affiliateLandingSteps,
  affiliateLandingSimulator,
  affiliateLandingFaq,
} from '../utils/affiliateLandingContent'

type CtaHandler = {
  onCtaClick: () => void
}

export default function AffiliateLandingPage() {
  const [open, setOpen] = React.useState(false)
  const [successOpen, setSuccessOpen] = React.useState(false)

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    }
  }, [])

  const handleCtaClick = React.useCallback(() => {
    setOpen(true)
  }, [])

  return (
    <div className="min-h-screen bg-background text-foreground">
      <LandingHeader />
      <main className="w-full pb-24">
        <AffiliateLandingHeroSection onCtaClick={handleCtaClick} />
        <AffiliateLandingStatsSection />
        <AffiliateLandingBenefitsSection />
        <AffiliateLandingHowItWorksSection onCtaClick={handleCtaClick} />
        <AffiliateLandingFaqSection />
        <AffiliateLandingFinalCtaSection onCtaClick={handleCtaClick} />
      </main>
      <LandingFooter />
      <AffiliateApplicationModal
        open={open}
        onOpenChange={setOpen}
        onSubmitted={() => setSuccessOpen(true)}
      />
      <SimpleFeedbackModal
        open={successOpen}
        onClose={() => setSuccessOpen(false)}
        title="Interesse enviado com sucesso"
        description={
          'Recebemos seus dados. Nosso time de afiliados ir\u00e1 analisar seu perfil e entrar em contato pelo e-mail informado.'
        }
        primaryLabel="Entendi"
      />
    </div>
  )
}

function AffiliateLandingHeroSection({ onCtaClick }: CtaHandler) {
  return (
    <section className="bg-gradient-to-br from-emerald-500/15 via-background to-background py-16 sm:py-24">
      <div className="mx-auto flex max-w-[1150px] flex-col gap-10 px-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="max-w-xl space-y-6">
          <span className="inline-flex items-center rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-400">
            Programa de Parceiros Cripto Alert
          </span>
          <div className="space-y-3">
            <h1 className="text-balance text-4xl font-semibold leading-tight sm:text-5xl md:text-6xl">
              {'Transforme sua audi\u00eancia em '}
              <span className="bg-gradient-to-r from-emerald-400 to-emerald-500 bg-clip-text text-transparent">
                renda recorrente
              </span>
              .
            </h1>
            <p className="text-base text-foreground/80 sm:text-lg">
              {
                'Estrutura completa para voc\u00ea indicar o Cripto Alert com transpar\u00eancia, acompanhar m\u00e9tricas e receber comiss\u00f5es conforme assinaturas confirmadas.'
              }
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Button className="h-11 rounded-full px-6" onClick={onCtaClick}>
              Quero ser parceiro
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-11 rounded-full px-6"
              onClick={onCtaClick}
            >
              Falar com o time
            </Button>
          </div>
          <p className="text-xs text-foreground/60">
            {
              'Ao enviar seus dados, voc\u00ea concorda em receber contato da equipe do Cripto Alert sobre o programa de afiliados.'
            }
          </p>
        </div>
        <div className="w-full max-w-md lg:max-w-sm">
          <Card>
            <CardHeader className="pb-3">
              <p className="text-sm font-medium">Exemplo de painel do afiliado</p>
              <p className="text-xs text-foreground/60">
                {
                  'Visualiza\u00e7\u00e3o ilustrativa de como voc\u00ea acompanha suas comiss\u00f5es dentro da plataforma.'
                }
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3">
                  <p className="text-xs text-foreground/70">{'Comiss\u00e3o total'}</p>
                  <p className="text-lg font-semibold text-emerald-400">R$ 12.450,00</p>
                </div>
                <div className="rounded-lg border border-border bg-card p-3">
                  <p className="text-xs text-foreground/70">{'Convers\u00e3o'}</p>
                  <p className="text-lg font-semibold">4,8%</p>
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-xs font-medium text-foreground/70">
                  {'\u00daltimas convers\u00f5es'}
                </p>
                <div className="space-y-1.5">
                  {['Novo assinante Pro', 'Upgrade para Pro', 'Assinante recorrente'].map(
                    (label, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between rounded-md border border-border/60 bg-card/60 px-3 py-2 text-xs"
                      >
                        <span>{label}</span>
                        <span className="font-semibold text-emerald-400">+ R$ 59,00</span>
                      </div>
                    ),
                  )}
                </div>
              </div>
              <p className="text-[11px] text-foreground/60">
                {
                  'Valores meramente ilustrativos para demonstrar o funcionamento do painel. O desempenho real depende das suas campanhas e da base de audi\u00eancia.'
                }
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  )
}

function AffiliateLandingStatsSection() {
  return (
    <section className="py-10" style={{ backgroundColor: '#1e1e1e' }}>
      <div className="mx-auto max-w-[1150px] px-6">
        <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
          {affiliateLandingStats.map((stat) => (
            <div key={stat.label} className="space-y-1">
              <p className="text-2xl font-semibold sm:text-3xl">{stat.value}</p>
              <p className="text-xs font-medium uppercase tracking-wide text-foreground/60">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function AffiliateLandingBenefitsSection() {
  const icons = [Wallet, TrendingUp, ShieldCheck, BarChart3, Users, PieChart]
  return (
    <section className="py-16">
      <div className="mx-auto max-w-[1150px] px-6">
        <div className="mb-10 text-center">
          <h2 className="text-3xl font-semibold sm:text-4xl">Por que ser nosso parceiro?</h2>
          <p className="mt-3 text-sm text-foreground/80 sm:text-base">
            {
              'Estrutura completa para voc\u00ea indicar o Cripto Alert com transpar\u00eancia, acompanhar m\u00e9tricas e receber comiss\u00f5es conforme assinaturas confirmadas.'
            }
          </p>
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          {affiliateLandingBenefits.map((benefit, idx) => {
            const Icon = icons[idx % icons.length]
            return (
              <Card key={benefit.key} className="h-full">
                <CardContent className="space-y-3 p-5">
                  <div className="inline-flex rounded-md bg-emerald-500/10 p-2 text-emerald-400">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-base font-semibold">{benefit.title}</h3>
                  <p className="text-sm text-foreground/80">{benefit.description}</p>
                </CardContent>
              </Card>
            )
          })}
        </div>
      </div>
    </section>
  )
}

function AffiliateLandingHowItWorksSection({ onCtaClick }: CtaHandler) {
  return (
    <section className="bg-card/30 py-16">
      <div className="mx-auto max-w-[1150px] px-6 lg:grid lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] lg:items-start lg:gap-12">
        <div className="space-y-8">
          <div className="space-y-2">
            <h2 className="text-3xl font-semibold sm:text-4xl">
              {'Como funciona na pr\u00e1tica'}
            </h2>
            <p className="text-sm text-foreground/80 sm:text-base">
              {
                'Do primeiro contato at\u00e9 o acompanhamento das comiss\u00f5es no painel, tudo foi desenhado para ser simples e transparente.'
              }
            </p>
          </div>
          <div className="space-y-6">
            {affiliateLandingSteps.map((item, idx) => (
              <div key={item.step} className="flex gap-4">
                <div className="mt-1 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500/10 text-xs font-semibold text-emerald-400">
                  {item.step}
                </div>
                <div className="space-y-1">
                  <h3 className="text-sm font-semibold sm:text-base">{item.title}</h3>
                  <p className="text-sm text-foreground/80">{item.text}</p>
                  {idx < affiliateLandingSteps.length - 1 && (
                    <div className="mt-3 h-px w-16 bg-border/60 sm:w-24" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-10 space-y-4 lg:mt-0">
          <Card>
            <CardHeader className="pb-3">
              <p className="text-sm font-medium">Simulador ilustrativo de ganhos</p>
              <p className="text-xs text-foreground/60">
                {
                  'Os n\u00fameros abaixo n\u00e3o representam promessa de resultado, apenas um exemplo de recorr\u00eancia.'
                }
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              {affiliateLandingSimulator.map((item) => (
                <div
                  key={item.label}
                  className="rounded-lg border border-border bg-background px-4 py-3 text-sm text-foreground/90"
                >
                  <p className="text-xs text-foreground/70">{item.label}</p>
                  <p className="mt-1 text-lg font-semibold text-emerald-400">{item.value}</p>
                  <p className="text-[11px] text-foreground/60">{item.helper}</p>
                </div>
              ))}
              <p className="text-[11px] text-foreground/60">
                {
                  'Valores dependem do plano contratado, das configura\u00e7\u00f5es de comiss\u00e3o ativas e do volume de clientes que chegam atrav\u00e9s das suas indica\u00e7\u00f5es.'
                }
              </p>
              <Button className="mt-2 w-full" onClick={onCtaClick}>
                Quero enviar meu interesse
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  )
}

function AffiliateLandingFaqSection() {
  return (
    <section className="py-16">
      <div className="mx-auto max-w-[900px] px-6">
        <div className="mb-8 text-center">
          <h2 className="text-2xl font-semibold sm:text-3xl">Perguntas frequentes</h2>
          <p className="mt-2 text-sm text-foreground/80 sm:text-base">
            {
              'D\u00favidas comuns de quem est\u00e1 avaliando entrar no programa de afiliados do Cripto Alert.'
            }
          </p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4 sm:p-6">
          {affiliateLandingFaq.map((item, index) => (
            <div
              key={item.q}
              className={index === affiliateLandingFaq.length - 1 ? 'border-b-0' : 'border-b border-border'}
            >
              <details className="group py-2">
                <summary className="flex cursor-pointer items-center justify-between py-2 text-left text-sm font-medium">
                  <span>{item.q}</span>
                  <span className="text-xl text-foreground/60 group-open:hidden">+</span>
                  <span className="hidden text-xl text-foreground/60 group-open:inline">-</span>
                </summary>
                <div className="pb-2 text-sm text-foreground/80">{item.a}</div>
              </details>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function AffiliateLandingFinalCtaSection({ onCtaClick }: CtaHandler) {
  return (
    <section className="bg-emerald-500 py-16 text-emerald-950">
      <div className="mx-auto max-w-[900px] px-6 text-center">
        <h2 className="text-3xl font-semibold sm:text-4xl">
          {'Pronto para come\u00e7ar a indicar?'}
        </h2>
        <p className="mt-3 text-sm font-medium sm:text-base">
          {
            'Envie seu interesse agora, sem compromisso. N\u00f3s analisamos seu perfil e mostramos o melhor caminho para conectar sua audi\u00eancia ao Cripto Alert.'
          }
        </p>
        <div className="mt-8 flex justify-center">
          <Button
            className="h-11 rounded-full bg-emerald-900 px-8 text-sm font-semibold text-emerald-50 hover:bg-emerald-950"
            onClick={onCtaClick}
          >
            Quero ser parceiro
          </Button>
        </div>
      </div>
    </section>
  )
}

